import { computed, inject } from '@angular/core';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '@shared/state/pagination.feature';
import { setError, setFulfilled, setPending, withRequestStatus } from '@shared/state/request-status.feature';
import { lastValueFrom, pipe, switchMap, tap } from 'rxjs';

import { CompromisedCard, MonitoredBin } from '../model/compromised-card.model';
import { CompromisedCardFilters } from '../model/compromised-card-filter-request.model';
import { BankCardService } from '../service/bank-card.service';

interface LoadRequest {
  pageNumber?: number;
  pageSize?: number;
  enableGlobalLoader?: boolean;
}

export const BankCardStore = signalStore(
  withDevtools('bank-card'),
  withState({
    data: null as CompromisedCard[] | null,
    totalElements: 0,
    totalPages: 0,
    pageNumber: DEFAULT_PAGE_NUMBER,
    pageSize: DEFAULT_PAGE_SIZE,
    filters: { orderBy: 'date', sortDirection: 'DESC' } as CompromisedCardFilters,
    monitoredBins: [] as MonitoredBin[],
  }),
  withRequestStatus(),
  withComputed(({ data, pageNumber, totalPages }) => ({
    cards: computed(() => data() ?? []),
    hasNextPage: computed(() => pageNumber() + 1 < totalPages()),
  })),
  withMethods((store, bankCardService = inject(BankCardService)) => {
    const load = rxMethod<LoadRequest>(
      pipe(
        tap(({ pageNumber, pageSize, enableGlobalLoader }) => {
          if (!enableGlobalLoader) {
            updateState(store, '[BankCardStore] Set pending', setPending());
          }
          updateState(store, '[BankCardStore] Set pagination', {
            pageNumber: pageNumber ?? store.pageNumber(),
            pageSize: pageSize ?? store.pageSize(),
          });
        }),
        switchMap(({ enableGlobalLoader }) =>
          bankCardService
            .getCards({
              ...store.filters(),
              pageNumber: store.pageNumber(),
              pageSize: store.pageSize(),
              enableGlobalLoader,
            })
            .pipe(
              tapResponse({
                next: ({ items, totalElements, totalPages }) => {
                  updateState(store, '[BankCardStore] Set fulfilled', setFulfilled());
                  updateState(store, '[BankCardStore] Set data', { data: items, totalElements, totalPages });
                },
                error: () => {
                  updateState(store, '[BankCardStore] Set error', setError());
                  updateState(store, '[BankCardStore] Set data', { data: null, totalElements: 0, totalPages: 0 });
                },
              }),
            ),
        ),
      ),
    );

    return {
      load,
      /** Any filter change restarts from the first page. */
      updateFilters(filters: Partial<CompromisedCardFilters>): void {
        updateState(store, '[BankCardStore] Update filters', { filters: { ...store.filters(), ...filters } });
        load({ pageNumber: DEFAULT_PAGE_NUMBER, enableGlobalLoader: true });
      },
      loadMonitoredBins: rxMethod<void>(
        pipe(
          switchMap(() =>
            bankCardService.getMonitoredBins().pipe(
              tapResponse({
                next: (monitoredBins) => updateState(store, '[BankCardStore] Set monitored bins', { monitoredBins }),
                error: () => updateState(store, '[BankCardStore] Set monitored bins', { monitoredBins: [] }),
              }),
            ),
          ),
        ),
      ),
      async downloadReportPdf(): Promise<void> {
        const response = await lastValueFrom(bankCardService.downloadReportPdf(store.filters()));

        if (response?.body) {
          const blobUrl = window.URL.createObjectURL(response.body);

          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = `bank-card-monitoring-${new Date().toISOString().slice(0, 10)}.pdf`;
          a.click();

          window.URL.revokeObjectURL(blobUrl);
        }
      },
    };
  }),
);
